import { useState, useRef, useCallback, useMemo } from 'react';

import {
  Badge,
  Button,
  DatatableCell,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Label,
  Spinner,
  Textarea,
} from '@monksflow/monks-ui';
import { ImageIcon, PlusIcon, UploadIcon } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { getFileUploadUrl, uploadFileToS3 } from '@/shared/lib/file-upload';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl/useGetDownloadUrl';
import {
  useCreateExternalTool,
  useDeleteExternalTool,
  useUpdateExternalTool,
} from '@/entities/external-tool';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { TableRow } from '@/shared/ui';
import { Table } from '@/shared/ui/Table';
import { TagInputField } from '@/shared/ui/form';
import { useTableData } from '@/shared/hooks/useTableData';
import type { ExternalToolCategoryDto, ExternalToolDto } from '@inference.engine/api-client/web';

import { RowMenu } from './RowMenu';
import { FormReseed } from './FormReseed';
import { BigSwitch } from '@/shared/ui/BigSwitch';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

// ─── Schema ──────────────────────────────────────────────────────────────────

// A factory, not a module-scope const: a schema built at import time would freeze its
// messages to whatever language was active then.
const buildExternalToolSchema = (t: TFunction<'validation'>) =>
  z.object({
    title: z.string().min(1, t('admin.categoriesPanel.titleIsRequired')).trim(),
    categories: z
      .array(z.string())
      .min(1, t('admin.externalToolsPanel.atLeastOneCategoryIsRequired')),
    description: z.string().min(1, t('admin.externalToolsPanel.descriptionIsRequired')).trim(),
    imageAssetId: z.string().optional(),
    url: z
      .url(t('admin.mcpCatalogPanel.mustBeAValidUrl'))
      .min(1, t('admin.externalToolsPanel.urlIsRequired')),
    isVisible: z.boolean(),
  });

type ExternalToolFormData = z.infer<ReturnType<typeof buildExternalToolSchema>>;

// ─── Types / constants ───────────────────────────────────────────────────────

interface ExternalToolRow {
  id: string;
  title: string;
  description: string;
  url: string;
  categories: string[];
  createdAt: string;
  isVisible: boolean;
  imageAssetId?: string;
  workspaceId?: string;
}

const buildColumns = (t: TFunction<'admin'>) => [
  {
    key: 'title' as const,
    label: t('admin.externalToolsPanel.title'),
    sortable: true,
    className: 'w-[60%]',
  },
  {
    key: 'categories' as const,
    label: t('admin.externalToolsPanel.category'),
    sortable: false,
    className: 'w-[20%]',
  },
  {
    key: 'isVisible' as const,
    label: t('admin.externalToolsPanel.visibility'),
    sortable: true,
    className: 'w-[20%]',
  },
  { key: 'id' as const, label: '', sortable: false, className: 'w-12.5' },
];

const DEFAULT_FORM: ExternalToolFormData = {
  title: '',
  categories: [],
  description: '',
  imageAssetId: undefined,
  url: '',
  isVisible: true,
};

function toRow(r: ExternalToolDto): ExternalToolRow {
  const cats = r.categories.map((cat) => cat.id);
  return {
    id: r.id,
    title: r.title,
    description: r.description || '',
    url: r.url || '',
    categories: cats,
    createdAt: r.createdAt,
    isVisible: r.isVisible,
    imageAssetId: r.imageAssetId,
    workspaceId: r.workspaceId,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const ExternalToolTitleCell = ({
  imageAssetId,
  title,
  description,
  isOrgWide,
}: {
  isOrgWide?: boolean;
  imageAssetId?: string;
  title: string;
  description: string;
}) => {
  const { t } = useTranslation('admin');

  const { downloadUrl } = useGetDownloadUrl(imageAssetId);
  return (
    <div className="flex flex-row items-center gap-3">
      <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded bg-muted">
        {downloadUrl ? (
          <img src={downloadUrl} alt={title} className="size-full object-cover" />
        ) : (
          <ImageIcon className="size-3.5 text-muted-foreground" />
        )}
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium leading-5">{title}</span>
        {description && (
          <span className="max-w-100 truncate text-xs leading-4 text-muted-foreground">
            {description}
          </span>
        )}
      </div>
      {isOrgWide && <Badge variant="secondary">{t('admin.externalToolsPanel.organization')}</Badge>}
    </div>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

interface ExternalToolsPanelProps {
  tools: ExternalToolDto[];
  toolsLoading: boolean;
  toolsFetching: boolean;
  allCategories: ExternalToolCategoryDto[];
  /**
   * Controlled add-modal open flag lifted to the parent (
   * replaces the imperative `onAdd(trigger)` registration effect).
   */
  addOpen?: boolean;
  onAddOpenChange?: (open: boolean) => void;
  workspaceId?: string;
}

export const ExternalToolsPanel = ({
  tools,
  toolsLoading,
  toolsFetching,
  allCategories,
  addOpen = false,
  onAddOpenChange,
  workspaceId,
}: ExternalToolsPanelProps) => {
  const { t } = useTranslation('admin');
  const { t: tValidation } = useTranslation('validation');
  const columns = useMemo(() => buildColumns(t), [t]);

  const createMutation = useCreateExternalTool();
  const updateMutation = useUpdateExternalTool();
  const deleteMutation = useDeleteExternalTool();

  const isWorkspaceTab = !!workspaceId;
  const isOrganizationTab = !isWorkspaceTab;

  const [modalOpenLocal, setModalOpenLocal] = useState(false);
  const [editingTool, setEditingTool] = useState<ExternalToolDto | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const modalOpen = modalOpenLocal || (addOpen && editingTool === null);

  const openAddModal = useCallback(() => {
    setEditingTool(null);
    onAddOpenChange?.(true);
  }, [onAddOpenChange]);

  const tableData = useTableData<ExternalToolRow>({
    data: tools.map(toRow),
    initialSort: { key: 'createdAt', direction: 'desc' },
    initialRowsPerPage: 25,
    searchFields: ['title', 'description'],
  });

  const formSchema = useMemo(() => buildExternalToolSchema(tValidation), [tValidation]);

  const form = useForm<ExternalToolFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: DEFAULT_FORM,
    // Submit-time rather than `onTouched`: every field here starts empty, so a
    // blur-time check answers a field nobody typed in, and each message grows
    // the centred dialog enough to slide the 16px close button out from under
    // the pointer — swallowing the very click that asked to close. Pressing the
    // CTA still surfaces every missing field at once.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const categoryIds = useMemo(() => allCategories.map((c) => c.id), [allCategories]);
  const categoryTitleById = useMemo(
    () => new Map(allCategories.map((c) => [c.id, c.title])),
    [allCategories],
  );

  const handleImageUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImageFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setImageUploading(true);
      try {
        const { url, fields, assetId } = await getFileUploadUrl(file.type, file.name);
        const formData = new FormData();
        Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
        formData.append('file', file);
        await uploadFileToS3(url, formData);
        form.setValue('imageAssetId', assetId, { shouldDirty: true });
        setImagePreview(URL.createObjectURL(file));
      } catch (error) {
        logger.error('Image upload failed', error);
        notificationService.showError(t('admin.externalToolsPanel.failedToUploadImage'));
      } finally {
        setImageUploading(false);
        e.target.value = '';
      }
    },
    [form, t],
  );

  // reseed the rhf form + preview when the (editingTool, modalOpen)
  // identity changes. `form.reset()` synchronously notifies its subscribers, so
  // running it from the render body made the mounted `Controller`s below call
  // setState during this panel's render. `<FormReseed>` (key-driven remount +
  // useMountEffect) moves it post-commit without a useEffect here.
  // eslint-disable-next-line i18next/no-literal-string -- React key discriminators, never rendered
  const toolFormKey = `${editingTool?.id ?? 'new'}|${modalOpen ? 'open' : 'closed'}`;
  const reseedForm = useCallback(() => {
    if (editingTool) {
      form.reset({
        title: editingTool.title || '',
        categories: editingTool.categories.map((cat) => cat.id),
        description: editingTool.description || '',
        imageAssetId: editingTool.imageAssetId || undefined,
        url: editingTool.url || '',
        isVisible: editingTool.isVisible,
      });
      setImagePreview(null);
    } else if (modalOpen) {
      form.reset(DEFAULT_FORM);
      setImagePreview(null);
    }
  }, [editingTool, modalOpen, form]);

  const watchedImageAssetId = form.watch('imageAssetId');
  const { downloadUrl: existingImageUrl } = useGetDownloadUrl(
    imagePreview ? undefined : watchedImageAssetId,
  );

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleClose = () => {
    setModalOpenLocal(false);
    setEditingTool(null);
    setImagePreview(null);
    form.reset(DEFAULT_FORM);
    onAddOpenChange?.(false);
  };

  const handleSubmit = form.handleSubmit((data) => {
    const body = {
      title: data.title,
      categoryIds: data.categories,
      description: data.description,
      imageAssetId: data.imageAssetId,
      url: data.url || undefined,
      isVisible: data.isVisible,
    };

    if (editingTool) {
      const id = editingTool.id;
      updateMutation.mutate({ id, body, workspaceId }, { onSuccess: handleClose });
    } else {
      createMutation.mutate({ body, workspaceId }, { onSuccess: handleClose });
    }
  });

  return (
    <>
      <FormReseed reseedKey={toolFormKey} onReseed={reseedForm} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => void handleImageFileChange(e)}
      />
      <div className="flex flex-col gap-6">
        {allCategories.length === 0 && !toolsLoading ? (
          <div className="flex flex-col items-center gap-4 py-10">
            <h3 className="text-xl leading-7 text-foreground">
              {t('admin.externalToolsPanel.noCategoriesAvailable')}
            </h3>
            <p className="text-sm leading-5 text-muted-foreground">
              {t('admin.externalToolsPanel.youNeedToCreateAt')}
            </p>
          </div>
        ) : tools.length === 0 && !toolsLoading ? (
          <div className="flex flex-col items-center gap-4 py-10">
            <h3 className="text-xl leading-7 text-foreground">
              {t('admin.externalToolsPanel.noExternalToolsYet')}
            </h3>
            <p className="text-sm leading-5 text-muted-foreground">
              {t('admin.externalToolsPanel.addExternalToolsThatUsers')}
            </p>
            <Button type="button" onClick={openAddModal}>
              <PlusIcon />
              {t('admin.externalToolsPanel.addExternalTool')}
            </Button>
          </div>
        ) : (
          <Table
            isLoading={toolsLoading}
            columns={columns}
            rows={tableData.paginatedData}
            sortBy={tableData.sortBy}
            sortDirection={tableData.sortDirection}
            onSort={tableData.toggleSort}
            pagination={{
              count: tableData.totalCount,
              page: tableData.page,
              rowsPerPage: tableData.rowsPerPage,
              onPageChange: tableData.handlePageChange,
              onRowsPerPageChange: tableData.handleRowsPerPageChange,
            }}
            renderBodyRow={(row) => {
              const isOrgWideRow = !row.workspaceId;
              const canEdit = !isOrgWideRow || isOrganizationTab;

              return (
                <TableRow key={row.id}>
                  <DatatableCell>
                    <ExternalToolTitleCell
                      imageAssetId={row.imageAssetId}
                      title={row.title}
                      description={row.description}
                      isOrgWide={isOrgWideRow}
                    />
                  </DatatableCell>
                  <DatatableCell>
                    <div className="flex flex-row flex-wrap gap-2">
                      {row.categories.map((id) => {
                        const title = categoryTitleById.get(id);
                        return title ? (
                          <Badge key={id} variant="secondary">
                            {title}
                          </Badge>
                        ) : (
                          <span key={id} className="text-xs italic leading-4 text-muted-foreground">
                            {t('admin.externalToolsPanel.unknown')}
                          </span>
                        );
                      })}
                    </div>
                  </DatatableCell>
                  <DatatableCell>
                    <BigSwitch
                      disabled={!canEdit}
                      checked={row.isVisible}
                      aria-label={t('admin.externalToolsPanel.visibleToUsers')}
                      onCheckedChange={(isVisible) =>
                        canEdit &&
                        updateMutation.mutate({
                          id: row.id,
                          body: { isVisible },
                          workspaceId,
                        })
                      }
                    />
                  </DatatableCell>
                  <DatatableCell>
                    {canEdit && (
                      <RowMenu
                        onEdit={
                          updateMutation.isPending || toolsFetching
                            ? () => {}
                            : () => {
                                const foundTool = tools.find((r) => r.id === row.id);
                                setEditingTool(foundTool ?? null);
                                setModalOpenLocal(true);
                              }
                        }
                        onDelete={() => deleteMutation.mutate({ id: row.id, workspaceId })}
                        deleteDisabled={deleteMutation.isPending}
                      />
                    )}
                  </DatatableCell>
                </TableRow>
              );
            }}
            containerClassName="p-0"
          />
        )}
      </div>

      <Dialog
        open={modalOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isSubmitting) handleClose();
        }}
      >
        <DialogContent className="max-w-160 gap-0 p-4" showCloseButton={!isSubmitting}>
          <DialogHeader>
            <DialogTitle>
              {editingTool
                ? editingTool.workspaceId
                  ? t('admin.externalToolsPanel.editWorkspaceExternalTool')
                  : t('admin.externalToolsPanel.editOrganizationExternalTool')
                : workspaceId
                  ? t('admin.externalToolsPanel.addWorkspaceExternalTool')
                  : t('admin.externalToolsPanel.addOrganizationExternalTool')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="flex gap-4 py-4">
            {/* Image upload */}
            <div className="flex flex-col gap-2">
              <span className="text-xs leading-4 text-muted-foreground">
                {t('admin.externalToolsPanel.image')}
              </span>
              <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
                {imageUploading ? (
                  <Spinner size="lg" />
                ) : imagePreview ? (
                  <img
                    src={imagePreview}
                    alt={t('admin.externalToolsPanel.preview')}
                    className="size-full object-cover"
                  />
                ) : existingImageUrl ? (
                  <img
                    src={existingImageUrl}
                    alt={t('admin.externalToolsPanel.externalTool')}
                    className="size-full object-cover"
                  />
                ) : watchedImageAssetId ? (
                  <Spinner size="lg" />
                ) : (
                  <ImageIcon className="size-8 text-muted-foreground" />
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleImageUploadClick}
                disabled={imageUploading}
              >
                <UploadIcon />
                {watchedImageAssetId
                  ? t('admin.externalToolsPanel.replace')
                  : t('admin.externalToolsPanel.upload')}
              </Button>
              {watchedImageAssetId && !imageUploading && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  onClick={() => {
                    form.setValue('imageAssetId', undefined, { shouldDirty: true });
                    setImagePreview(null);
                  }}
                >
                  {t('admin.externalToolsPanel.remove')}
                </Button>
              )}
            </div>

            <div className="flex flex-1 flex-col gap-6">
              <Field data-invalid={form.formState.errors.title ? true : undefined}>
                <FieldLabel htmlFor="external-tool-title">
                  {t('admin.externalToolsPanel.title')}
                  <span aria-hidden="true" className="text-destructive">
                    *
                  </span>
                </FieldLabel>
                <Input
                  id="external-tool-title"
                  required
                  placeholder={t('admin.externalToolsPanel.enterExternalToolTitle')}
                  aria-invalid={!!form.formState.errors.title}
                  {...form.register('title')}
                />
                {form.formState.errors.title && (
                  <FieldError>{form.formState.errors.title.message}</FieldError>
                )}
              </Field>

              <Controller
                name="categories"
                control={form.control}
                render={({ field, fieldState }) => (
                  <TagInputField<string>
                    id="external-tool-categories"
                    label={t('admin.externalToolsPanel.categories')}
                    required
                    value={field.value}
                    onChange={field.onChange}
                    options={categoryIds}
                    getOptionLabel={(id) => categoryTitleById.get(id) ?? id}
                    getOptionValue={(id) => id}
                    error={fieldState.error?.message}
                  />
                )}
              />

              <Field data-invalid={form.formState.errors.description ? true : undefined}>
                <FieldLabel htmlFor="external-tool-description">
                  {t('admin.externalToolsPanel.description')}
                  <span aria-hidden="true" className="text-destructive">
                    *
                  </span>
                </FieldLabel>
                <Textarea
                  id="external-tool-description"
                  required
                  rows={3}
                  placeholder={t('admin.externalToolsPanel.enterExternalToolDescription')}
                  aria-invalid={!!form.formState.errors.description}
                  {...form.register('description')}
                />
                {form.formState.errors.description && (
                  <FieldError>{form.formState.errors.description.message}</FieldError>
                )}
              </Field>

              <Field data-invalid={form.formState.errors.url ? true : undefined}>
                <FieldLabel htmlFor="external-tool-url">
                  {t('admin.externalToolsPanel.externalToolUrl')}
                  <span aria-hidden="true" className="text-destructive">
                    *
                  </span>
                </FieldLabel>
                <Input
                  id="external-tool-url"
                  required
                  placeholder="https://example.com"
                  aria-invalid={!!form.formState.errors.url}
                  {...form.register('url')}
                />
                {form.formState.errors.url ? (
                  <FieldError>{form.formState.errors.url.message}</FieldError>
                ) : null}
              </Field>
              <p className="text-xs leading-4 text-muted-foreground">
                {t('admin.externalToolsPanel.theExternalLinkThatOpens')}
              </p>

              <Controller
                name="isVisible"
                control={form.control}
                render={({ field }) => (
                  <div className="flex items-center gap-2">
                    <BigSwitch
                      id="external-tool-is-visible"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <Label htmlFor="external-tool-is-visible">
                      {t('admin.externalToolsPanel.visibleToUsers')}
                    </Label>
                  </div>
                )}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('admin.externalToolsPanel.cancel')}
            </Button>
            <Button type="button" onClick={() => void handleSubmit()} disabled={isSubmitting}>
              {/* Hidden from the a11y tree so the button keeps its own name. */}
              {isSubmitting && <Spinner aria-hidden="true" />}
              {editingTool
                ? t('admin.externalToolsPanel.update')
                : t('admin.externalToolsPanel.addExternalTool')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
